import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, CheckCircle, Archive, ClockCounterClockwise } from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import ContextCarryOver from "@/components/ContextCarryOver";

const STEPS = [
  { n: 1, code: "1", key: "identify", label: "Identify", phase: "OPERATIONS", placeholder: "Locate and isolate the single system constraint — machine, approval loop, or resource — dragging down throughput." },
  { n: 2, code: "2", key: "exploit", label: "Exploit", phase: "OPERATIONS", placeholder: "How to run the bottleneck at maximum capacity immediately using zero additional capital or headcount." },
  { n: 3, code: "3", key: "subordinate", label: "Subordinate", phase: "OPERATIONS", placeholder: "How to throttle or align all non-bottleneck processes to match the pace of the constraint." },
  { n: 4, code: "4", key: "elevate", label: "Elevate", phase: "OPERATIONS", placeholder: "Capital investment, structural changes or added resources required if the constraint must be broken." },
  { n: 5, code: "5", key: "repeat", label: "Repeat", phase: "OPERATIONS", placeholder: "Run the diagnostic loop again — where has the new bottleneck migrated?" },
];
const LAST = STEPS.length;
const empty = { identify: "", exploit: "", subordinate: "", elevate: "", repeat: "" };
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

export default function Toc() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [data, setData] = useState(empty);
  const [open, setOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const history = data.version_history || [];
  const activeVersion = history.length + 1;

  useEffect(() => {
    (async () => {
      try { const res = await api.get(`/documents/${id}`); setTitle(res.data.title || ""); setData({ ...empty, ...(res.data.payload || {}) }); }
      catch { toast.error("Document not found"); navigate("/dashboard"); }
      finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try { await api.put(`/documents/${id}`, { title: title || "Untitled Constraint Analysis", payload: data, ...extra }); return true; }
    catch { toast.error("Save failed"); return false; }
    finally { setSaving(false); }
  }, [id, title, data]);

  const cur = STEPS[step - 1];
  const set = (v) => setData((d) => ({ ...d, [cur.key]: v }));
  const saveDraft = async () => { if (await persist()) toast.success("Draft saved"); };
  const next = async () => { await persist(); setStep((s) => Math.min(LAST, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const finalise = async () => { if (await persist({ status: "Completed" })) { toast.success("Constraint analysis finalised"); navigate(`/toc/${id}/view`); } };
  const applySuggestion = (text) => { setData((d) => { const e = d[cur.key]; return { ...d, [cur.key]: !e || !e.trim() ? text : `${e.trimEnd()}\n- ${text}` }; }); toast.success("Suggestion added"); };
  const onPull = ({ title: t, context }) => { setData((d) => ({ ...d, identify: context || d.identify })); if (!title && t) setTitle(t); toast.success("Context pulled in"); };

  const archiveMigration = async () => {
    setArchiving(true);
    try {
      // Persist any unsaved edits first so the snapshot is faithful, then archive on the server.
      await persist();
      const res = await api.post(`/documents/${id}/toc-archive`);
      setData({ ...empty, ...(res.data.payload || {}) });
      const v = (res.data.payload?.version_history?.length || 0) + 1;
      toast.success(`Migration tracked — archived Version ${v - 1}, now editing Version ${v}`);
    } catch { toast.error("Archive failed"); }
    finally { setArchiving(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-40 text-slate-400"><CircleNotch size={28} className="animate-spin" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto w-full py-8 px-6 lg:px-10">
        <button data-testid="back-link" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft size={16} /> Workspace</button>
        <input data-testid="toc-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Constraint analysis title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1" />

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="toc-stepper">
          {STEPS.map((s) => (
            <button key={s.n} data-testid={`step-${s.n}`} onClick={() => setStep(s.n)}
              className={`rounded-sm border px-3 py-2.5 text-left transition-all ${step === s.n ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <span className={`font-mono text-xs font-bold ${step === s.n ? "text-blue-700" : "text-slate-400"}`}>STEP {s.n}</span>
              <p className={`text-xs font-medium ${step === s.n ? "text-slate-900" : "text-slate-500"}`}>{s.label}</p>
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded-sm px-4 py-3" data-testid="toc-migration-bar">
          <div className="flex items-center gap-2 text-sm text-amber-900">
            <ClockCounterClockwise size={18} weight="duotone" className="text-amber-700 shrink-0" />
            <span data-testid="toc-active-version" className="font-mono font-bold">Version {activeVersion}</span>
            <span className="text-amber-700">·</span>
            <span className="text-amber-800">{history.length} archived snapshot{history.length === 1 ? "" : "s"}</span>
          </div>
          <button data-testid="toc-archive-migration" onClick={archiveMigration} disabled={archiving || saving}
            className="inline-flex items-center justify-center gap-2 rounded-sm border border-amber-300 bg-white px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50">
            {archiving ? <CircleNotch size={16} className="animate-spin" /> : <Archive size={16} weight="duotone" />} Archive &amp; Track Migration
          </button>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm shadow-sm p-6 space-y-4">
          <h2 className="text-xl font-semibold text-slate-900">{cur.label}</h2>
          {step === 1 && <ContextCarryOver currentId={id} onPull={onPull} />}
          <textarea data-testid={`field-${cur.key}`} rows={9} className={taCls} value={data[cur.key]} onChange={(e) => set(e.target.value)} placeholder={cur.placeholder} />
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="toc-back" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"><ArrowLeft size={16} /> Back</button>
          <div className="flex items-center gap-3">
            <button data-testid="toc-save-draft" onClick={saveDraft} disabled={saving} className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">{saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft</button>
            {step < LAST ? (
              <button data-testid="toc-next" onClick={next} className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">Next Step <ArrowRight size={16} /></button>
            ) : (
              <button data-testid="toc-finalise" onClick={finalise} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700"><CheckCircle size={18} weight="fill" /> Finalise Analysis</button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={open} onClick={() => setOpen(true)} phase={cur.phase} />
      <CoPilotPanel open={open} onClose={() => setOpen(false)} docId={id} toolType="toc" step={cur} phase={cur.phase} onApplySuggestion={applySuggestion} />
    </Layout>
  );
}
